import { useEffect, useState } from 'react';
import { subscribe } from 'valtio';

const subscriptions = new WeakMap();

function sameId(left, right) {
  return String(left) === String(right);
}

export function readCustomizationState(store, controllerId, parentId, componentId, defaultVisible = true) {
  return {
    editMode: Boolean(store.editMode),
    visible: store.profile?.[controllerId]?.[parentId]?.[componentId] ?? defaultVisible
  };
}

export function isCustomizationChange(operations, controllerId, parentId, componentId) {
  return operations.some((operation) => {
    const path = operation[1] ?? [];

    if (path[0] === 'editMode') return true;
    if (path[0] !== 'profile') return false;
    if (path.length === 1) return true;
    if (!sameId(path[1], controllerId)) return false;
    if (path.length === 2) return true;
    if (!sameId(path[2], parentId)) return false;
    return path.length === 3 || sameId(path[3], componentId);
  });
}

function subscribeToCustomization(store, listener) {
  let subscription = subscriptions.get(store);

  if (!subscription) {
    subscription = { listeners: new Set(), unsubscribe: null };
    subscription.unsubscribe = subscribe(
      store,
      (operations) => {
        subscription.listeners.forEach((registeredListener) => {
          if (
            isCustomizationChange(
              operations,
              registeredListener.controllerId,
              registeredListener.parentId,
              registeredListener.componentId
            )
          ) {
            registeredListener.notify();
          }
        });
      },
      true
    );
    subscriptions.set(store, subscription);
  }

  subscription.listeners.add(listener);

  return () => {
    subscription.listeners.delete(listener);
    if (subscription.listeners.size === 0) {
      subscription.unsubscribe();
      subscriptions.delete(store);
    }
  };
}

export default function useCustomizationState(store, controllerId, parentId, componentId, defaultVisible = true) {
  const [customizationState, setCustomizationState] = useState(() =>
    readCustomizationState(store, controllerId, parentId, componentId, defaultVisible)
  );

  useEffect(() => {
    const update = () => {
      const next = readCustomizationState(store, controllerId, parentId, componentId, defaultVisible);
      setCustomizationState((current) =>
        current.editMode === next.editMode && current.visible === next.visible ? current : next
      );
    };
    const listener = { controllerId, parentId, componentId, notify: update };

    update();
    return subscribeToCustomization(store, listener);
  }, [store, controllerId, parentId, componentId, defaultVisible]);

  return customizationState;
}
